import { useState, useRef } from "react";
import { useDispatch } from "react-redux";
import { v4 as uuidv4 } from "uuid";

import {
  IconCheck,
  IconChevronDown,
  IconFile,
  IconUpload,
  IconX,
  Spinner,
  StatCard,
} from "./iconsSvg";
import { useImportPpto } from "../../hooks/useImportPpto";
import { initialAddItems, initialAddTitles } from "../../helpers/items";
import { iniUpdateBudget } from "../../redux/actions/pptos";

export default function ImportItemsPptos({ open, setOpen, detail }) {
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [ayudaAbierta, setAyuda] = useState(false);
  const inputRef = useRef();

  const dispatch = useDispatch();

  const {
    loading,
    error,
    resultado,
    reset,
    downloadItemTemplate,
    handleImportarExcel,
    setLoading,
  } = useImportPpto();

  const handleFile = async (f) => {
    if (!f) return;
    const ext = f.name.split(".").pop().toLowerCase();
    if (!["xlsx", "xls"].includes(ext)) return;
    reset();
    setFile(f);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFile(e.dataTransfer.files[0]);
  };

  const handleSubmit = async () => {
    setLoading(true);

    if (!file) return;

    const itemsImported = await handleImportarExcel(file);

    let newItems = [];
    let currentTitleId = null;
    for (let x = 0; x < itemsImported.length; x++) {
      const item = itemsImported[x];
      const isTitle =
        !item.unidad &&
        !item.cantidad &&
        !item.precio_unitario &&
        !item.costo_unitario;

      if (isTitle) {
        currentTitleId = uuidv4();

        const newData = {
          id: currentTitleId,
          nombre: item.descripcion,
          items: [],
        };

        const newTitle = await initialAddTitles(currentTitleId, newData);
        newItems.push(newTitle);
      } else {
        const newData = {
          id: uuidv4(),
          nombre: item.descripcion,
          unidad_medida: item.unidad?.toString().toUpperCase(),
          cant: item.cantidad,
          precio_unitario: item.precio_unitario,
          costo_unitario: item.costo_unitario,
          id_titulo: currentTitleId,
        };

        const newItem = await initialAddItems({
          newId: newData.id,
          idTitle: currentTitleId,
          sorting: null,
          data: newData,
          categoryId: null,
        });

        if (newItems.length === 0) {
          // Si el primer ítem no es título, creamos uno genérico
          const newTitle = {
            id: uuidv4(),
            nombre: "Título genérico",
            items: [],
          };

          newItems.push(newTitle);
        }

        newItems[newItems.length - 1].items.push(newItem);
      }
    }

    // dispatch(budgetsLoadItems(newItems));

    const newDetail = {
      ...detail,
      items: newItems,
    };

    setLoading(false);
    setOpen(false);

    dispatch(iniUpdateBudget(newDetail, newDetail.id));
  };

  const handleReset = () => {
    reset();
    setFile(null);
  };

  const formatMoney = (n) =>
    n != null
      ? `S/ ${Number(n).toLocaleString("es-PE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
      : "—";

  return (
    <div className="flex max-w-[560px] flex-col gap-4 mx-auto">
      {/* Descripción */}
      <p className="text-[14px] leading-[18px] text-[#909090] text-center mt-5">
        Importa tu presupuesto desde Excel. Los ítems se cargarán
        automáticamente.
      </p>

      {/* ── Drop zone ── */}
      {!resultado && (
        <div
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          className={
            "flex cursor-pointer flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed px-6 py-10 transition-all " +
            (dragOver || file
              ? "border-[#8E24AA] bg-[#FCF1FF]"
              : "border-[#EDEDED] bg-[#F9F9F9] hover:border-[#8E24AA] hover:bg-[#FCF1FF]")
          }
        >
          <input
            ref={inputRef}
            type="file"
            accept=".xlsx,.xls"
            className="hidden"
            onChange={(e) => handleFile(e.target.files[0])}
          />

          {file ? (
            <>
              <div className="flex size-12 items-center justify-center rounded-full bg-[#8E24AA]">
                <IconFile className="size-6 text-white" />
              </div>
              <div className="flex flex-col items-center gap-1 text-center">
                <span className="text-[14px] font-medium text-[#595759]">
                  {file.name}
                </span>
                <span className="text-[12px] text-[#909090]">
                  {(file.size / 1024).toFixed(0)} KB · haz clic para cambiar
                </span>
              </div>
            </>
          ) : (
            <>
              <div className="flex size-12 items-center justify-center rounded-full bg-[#EDEDED]">
                <IconUpload className="size-6 text-[#909090]" />
              </div>
              <div className="flex flex-col items-center gap-1 text-center">
                <span className="text-[14px] font-medium text-[#595759]">
                  Arrastra tu Excel aquí o haz clic para seleccionar
                </span>
                <span className="text-[12px] text-[#909090]">
                  Formatos aceptados: .xlsx, .xls · máx. 10 MB
                </span>
              </div>
            </>
          )}
        </div>
      )}

      {/* ── Error ── */}
      {error && (
        <div className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3">
          <div className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full bg-red-100">
            <IconX className="size-3 text-red-600" />
          </div>
          <p className="text-[14px] leading-[18px] text-red-700">{error}</p>
        </div>
      )}

      {/* ── Resultado exitoso ── */}
      {resultado && (
        <div className="flex flex-col gap-4 rounded-lg border border-[#EDEDED] bg-white p-5 shadow-[0px_4px_4px_0px_rgba(216,216,216,0.25)]">
          <div className="flex items-start gap-3 border-b border-[#EDEDED] pb-4">
            <div className="flex size-11 shrink-0 items-center justify-center rounded-full bg-[#FCF1FF]">
              <IconCheck className="size-5 text-[#8E24AA]" />
            </div>
            <div className="flex flex-col gap-0.5">
              <span className="text-[14px] font-medium text-[#595759]">
                Presupuesto importado correctamente
              </span>
              <span className="text-[12px] leading-[18px] text-[#909090]">
                El presupuesto ya está disponible en el proyecto.
              </span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <StatCard label="Secciones" value={resultado.secciones} />
            <StatCard label="Ítems" value={resultado.items} />
            <StatCard
              label="Costo directo"
              value={formatMoney(resultado.costoTotal)}
            />
            <StatCard
              label="Total con IGV"
              value={formatMoney(resultado.totalConIgv)}
            />
          </div>

          <div>
            <button
              onClick={handleReset}
              className="rounded border border-[#8E24AA] bg-white px-4 py-2 text-[14px] text-[#8E24AA] shadow-[0px_4px_4px_0px_rgba(216,216,216,0.25)] transition-colors hover:bg-[#FCF1FF]"
            >
              Importar otro archivo
            </button>
          </div>
        </div>
      )}

      {/* ── Botón importar ── */}
      {!resultado && (
        <button
          onClick={handleSubmit}
          disabled={!file || loading}
          className={
            "flex w-full items-center justify-center gap-2 rounded-lg py-[10px] text-[14px] font-medium transition-colors " +
            (!file || loading
              ? "cursor-not-allowed bg-[#D1C4E9] text-white"
              : "cursor-pointer bg-[#8E24AA] text-white hover:bg-[#7B1FA2]")
          }
        >
          {loading ? (
            <>
              <Spinner /> Procesando...
            </>
          ) : (
            <>
              <IconUpload className="size-4" /> Importar items presupuesto
            </>
          )}
        </button>
      )}

      {/* ── Ayuda formato ── */}
      {!resultado && (
        <div className="overflow-hidden rounded-lg border border-[#EDEDED] bg-white">
          <button
            onClick={() => setAyuda(!ayudaAbierta)}
            className="flex w-full items-center justify-between px-4 py-3 text-left"
          >
            <span className="text-[14px] font-medium text-[#595759]">
              ¿Qué formato debe tener el Excel?
            </span>
            <IconChevronDown
              className={
                "size-4 text-[#909090] transition-transform " +
                (ayudaAbierta ? "rotate-180" : "")
              }
            />
          </button>

          {ayudaAbierta && (
            <div className="border-t border-[#EDEDED] px-4 pb-4 pt-3">
              <p className="mb-2 text-[12px] font-medium text-[#595759]">
                Columnas requeridas (en ese orden):
              </p>
              <code className="mb-3 block rounded bg-[#F9F9F9] px-3 py-2 text-[12px] text-[#595759]">
                ITEM | UNIDAD | CANTIDAD | PRECIO UNITARIO | COSTO UNITARIO
              </code>
              <ul className="flex flex-col gap-2">
                {[
                  "El total de cada item es calculado automáticamente. De acuerdo con la cantidad y el precio unitario o costo unitario. No es necesario incluirlo.",
                  "Los ítems con decimal se ingresan con puntos Ej:(1.01, 1.02). Ya sea para cantidad, precio unitario o costo unitario.",
                  "El total global del presupuesto se calcula automáticamente. No es necesario incluirlo.",
                ].map((txt, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-2 text-[12px] leading-[18px] text-[#909090]"
                  >
                    <span className="mt-1.5 size-1.5 shrink-0 rounded-full bg-[#8E24AA]" />
                    {txt}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      <div className="p-4 bg-white rounded-xl shadow-sm border border-slate-200">
        <h3 className="text-lg font-semibold text-slate-800 mb-2">
          Importar Items 📊
        </h3>
        <p className="text-sm text-slate-500 mb-4">
          Si tienes tu presupuesto en Excel, descárgate nuestra plantilla para
          asegurar que el formato sea el correcto.
        </p>

        <div className="flex flex-wrap gap-3">
          {/* BOTÓN PARA DESCARGAR */}
          <button
            onClick={downloadItemTemplate}
            className="flex items-center gap-2 px-4 py-2 text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg font-medium transition-colors border border-blue-200"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M4 16v1a2 2 0 002 2h12a2 2 0 002-2v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
              />
            </svg>
            Descargar Plantilla
          </button>

          {/* AQUÍ IRÍA TU INPUT FILE PARA SUBIR */}
        </div>
      </div>
    </div>
  );
}
